import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Collapse,
  Button,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Error,
  ExpandMore,
  ExpandLess,
  Info,
  ErrorOutline,
  WarningAmber,
  CheckCircleOutline,
} from '@mui/icons-material';

export type DiagnosticStatus = 'success' | 'failed' | 'warning' | 'error';

interface DiagnosticCardProps {
  title: string;
  status: DiagnosticStatus;
  message: string;
  data?: any;
  isLoading?: boolean;
}

const DiagnosticCard: React.FC<DiagnosticCardProps> = ({
  title,
  status,
  message,
  data,
  isLoading = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  console.log(`🎯 DiagnosticCard "${title}": isLoading=${isLoading}, status=${status}`);
  
  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#66BB6A'; // Lighter green
      case 'failed':
        return '#F44336'; // Darker red for better contrast
      case 'warning':
        return '#FF9800'; // Orange for warning
      case 'error':
        return '#F44336'; // Same as failed for consistency
      default:
        return '#9E9E9E';
    }
  };

  const getStatusBackgroundColor = () => {
    switch (status) {
      case 'success':
        return '#F1F8E9'; // Very light green background
      case 'failed':
        return '#fcdcdc'; // Slightly darker red background
      case 'warning':
        return '#FFF3E0'; // Very light orange background
      case 'error':
        return '#fcdcdc'; // Same as failed for consistency
      default:
        return '#F5F5F5';
    }
  };

  const getStatusIcon = () => {
    const iconStyle = { fontSize: 24 };
    switch (status) {
      case 'success':
        return <CheckCircle sx={{ ...iconStyle, color: '#66BB6A' }} />;
      case 'failed':
        return <Cancel sx={{ ...iconStyle, color: '#F44336' }} />;
      case 'warning':
        return <Warning sx={{ ...iconStyle, color: '#FF9800' }} />;
      case 'error':
        return <Error sx={{ ...iconStyle, color: '#F44336' }} />;
      default:
        return <Error sx={{ ...iconStyle, color: '#9E9E9E' }} />;
    }
  };

  const getStatusChipColor = () => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDetailedFailureInfo = () => {
    if (status === 'success') return null;

    // Use actual data from the diagnostic result instead of static content
    const failureInfo: { title: string; details: string[]; recommendations: string[] } = {
      title: '',
      details: [],
      recommendations: []
    };

    // Extract information from the actual data
    if (data) {
      // Set title based on the diagnostic type
      switch (title) {
        case 'Number Mapping Check':
          failureInfo.title = data.failureReason || 'Phone Number Not Mapped';
          failureInfo.details = data.possibleReasons || [
            'The phone number is not registered in the messaging platform',
            'No mapping found between phone number and messaging application',
            'Missing enterprise or user configuration'
          ];
          failureInfo.recommendations = data.recommendations || [
            'Contact system administrator to map the phone number',
            'Verify phone number format and registration',
            'Check enterprise configuration in the database'
          ];
          break;

        case 'SMS Enablement':
          failureInfo.title = data.failureReason || 'SMS Capabilities Not Enabled';
          failureInfo.details = data.possibleReasons || [
            'SMS features are disabled on this phone number',
            'Carrier has not enabled SMS for this number',
            'Account may not have SMS permissions'
          ];
          failureInfo.recommendations = data.recommendations || [
            'Contact carrier to enable SMS on the phone number',
            'Verify account has SMS permissions',
            'Check carrier-specific SMS requirements'
          ];
          break;

        case 'Recent Error Analysis':
          failureInfo.title = data.failureReason || 'Recent Delivery Errors Detected';
          failureInfo.details = data.possibleReasons || [
            'Multiple delivery failures in the last 24 hours',
            'Carrier rejections or network issues detected',
            'Message delivery rate below acceptable threshold'
          ];
          failureInfo.recommendations = data.recommendations || [
            'Review error logs for specific failure reasons',
            'Check carrier status and network connectivity',
            'Verify message content compliance'
          ];
          break;

        case '10DLC Campaign Status':
          failureInfo.title = data.failureReason || '10DLC Campaign Issues';
          failureInfo.details = data.possibleReasons || [
            'Campaign not registered or inactive',
            'Missing compliance requirements',
            'Campaign approval pending or rejected'
          ];
          failureInfo.recommendations = data.recommendations || [
            'Complete 10DLC campaign registration',
            'Update campaign compliance information',
            'Contact TCR for campaign approval'
          ];
          break;

        case 'Carrier Approval Status':
          failureInfo.title = data.failureReason || 'Carrier Approval Issues';
          failureInfo.details = data.possibleReasons || [
            'Dynamic Campaign Approval (DCA) not registered',
            'Missing carrier-specific approvals',
            'Campaign ID not available for approval check'
          ];
          failureInfo.recommendations = data.recommendations || [
            'Complete DCA registration with carriers',
            'Verify campaign ID and registration status',
            'Contact carriers for approval process'
          ];
          break;

        default:
          failureInfo.title = data.failureReason || 'Diagnostic Check Failed';
          failureInfo.details = data.possibleReasons || ['Unknown failure reason'];
          failureInfo.recommendations = data.recommendations || ['Contact support for assistance'];
      }
    } else {
      // Fallback to static content if no data is available
      switch (title) {
        case 'Number Mapping Check':
          failureInfo.title = 'Phone Number Not Mapped';
          failureInfo.details = [
            'The phone number is not registered in the messaging platform',
            'No mapping found between phone number and messaging application',
            'Missing enterprise or user configuration'
          ];
          failureInfo.recommendations = [
            'Contact system administrator to map the phone number',
            'Verify phone number format and registration',
            'Check enterprise configuration in the database'
          ];
          break;

        case 'SMS Enablement':
          failureInfo.title = 'SMS Capabilities Not Enabled';
          failureInfo.details = [
            'SMS features are disabled on this phone number',
            'Carrier has not enabled SMS for this number',
            'Account may not have SMS permissions'
          ];
          failureInfo.recommendations = [
            'Contact carrier to enable SMS on the phone number',
            'Verify account has SMS permissions',
            'Check carrier-specific SMS requirements'
          ];
          break;

        case 'Recent Error Analysis':
          failureInfo.title = 'Recent Delivery Errors Detected';
          failureInfo.details = [
            'Multiple delivery failures in the last 24 hours',
            'Carrier rejections or network issues detected',
            'Message delivery rate below acceptable threshold'
          ];
          failureInfo.recommendations = [
            'Review error logs for specific failure reasons',
            'Check carrier status and network connectivity',
            'Verify message content compliance'
          ];
          break;

        case '10DLC Campaign Status':
          failureInfo.title = '10DLC Campaign Issues';
          failureInfo.details = [
            'Campaign not registered or inactive',
            'Missing compliance requirements',
            'Campaign approval pending or rejected'
          ];
          failureInfo.recommendations = [
            'Complete 10DLC campaign registration',
            'Update campaign compliance information',
            'Contact TCR for campaign approval'
          ];
          break;

        case 'Carrier Approval Status':
          failureInfo.title = 'Carrier Approval Issues';
          failureInfo.details = [
            'Dynamic Campaign Approval (DCA) not registered',
            'Missing carrier-specific approvals',
            'Campaign ID not available for approval check'
          ];
          failureInfo.recommendations = [
            'Complete DCA registration with carriers',
            'Verify campaign ID and registration status',
            'Contact carriers for approval process'
          ];
          break;

        default:
          failureInfo.title = 'Diagnostic Check Failed';
          failureInfo.details = ['Unknown failure reason'];
          failureInfo.recommendations = ['Contact support for assistance'];
      }
    }

    return failureInfo;
  };

  const renderDataDetails = () => {
    if (!data) return null;

    // For Recent Error Analysis, don't show any data details
    if (title === 'Recent Error Analysis') {
      return null;
    }

    // For other cards, only show data for failed/warning/error cases
    if (status === 'success') return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          Response Data:
        </Typography>
        
        {/* Show only the raw response data */}
        <Box sx={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.05)', 
          p: 2, 
          borderRadius: 2,
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          border: '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </Box>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        backgroundColor: getStatusBackgroundColor(),
        border: `1px solid ${getStatusColor()}`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 32px ${getStatusColor()}20`,
        },
      }}
    >
      <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              flex: 1,
              pr: 2,
            }}
          >
            {title}
          </Typography>
          {isLoading ? (
            <CircularProgress size={32} color="primary" />
          ) : (
            getStatusIcon()
          )}
        </Box>

        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            mb: 3,
            minHeight: '24px',
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          {isLoading ? 'Checking...' : message}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: status === 'success' ? '#66BB6A' : status === 'warning' ? '#FF9800' : '#F44336',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              borderRadius: 2,
              px: 2,
              py: 0.5,
              '&:hover': {
                backgroundColor: status === 'success' ? '#4CAF50' : status === 'warning' ? '#F57C00' : '#D32F2F',
              },
            }}
          >
            {isLoading ? 'LOADING' : status.toUpperCase()}
          </Button>
          
          {/* Show expand button for failed/warning cards only */}
          {(status === 'failed' || status === 'warning' || status === 'error') && !isLoading && !(title === 'Recent Error Analysis' && status === 'warning') && (
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              sx={{ 
                minWidth: 'auto', 
                p: 1,
                color: getStatusColor(),
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: `${getStatusColor()}25`,
                },
              }}
            >
              {expanded ? 'Hide Details' : 'Show Details'}
            </Button>
          )}
        </Box>

        {/* Expandable detailed information */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${getStatusColor()}40` }}>
            {renderDataDetails()}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default DiagnosticCard; 
